import React, { useRef } from 'react';
import { X, Thermometer, Pencil, Check } from 'lucide-react';
import { useGetZoneSensorsQuery } from '../../services/zoneApi';
import { useUpdateSensorMutation } from '../../services/sensorApi';
import SensorAssignment from './SensorAssignment';
import { formatSensorSerial } from '../../utils/sensorUtils';
import type { Sensor } from '../../types';

interface ZoneSensorsProps {
  zoneId: number;
  sensors: Sensor[];
  onAssign: (zoneId: number, sensorId: number) => void | Promise<void>;
  onRemove: (zoneId: number, sensorId: number) => void | Promise<void>;
  zonesLoading?: boolean;
}

export default function ZoneSensors({ zoneId, sensors, onAssign, onRemove, zonesLoading }: ZoneSensorsProps) {
  const { data: zoneSensors, isLoading } = useGetZoneSensorsQuery(zoneId);

  const [selectedSensorIds, setSelectedSensorIds] = React.useState<number[]>([]);
  const [editingSensorId, setEditingSensorId] = React.useState<number | null>(null);
  const [editingSensorName, setEditingSensorName] = React.useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const [updateSensor] = useUpdateSensorMutation();

  const handleStartSensorEdit = (sensorId: number, currentName: string) => {
    setEditingSensorId(sensorId);
    setEditingSensorName(currentName);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const handleCancelSensorEdit = () => {
    setEditingSensorId(null);
    setEditingSensorName('');
  };

  const handleSaveSensorEdit = async (sensorId: number) => {
    const trimmed = editingSensorName.trim();
    if (!trimmed) return;
    try {
      await updateSensor({ sensorId, sensorName: trimmed }).unwrap();
      setEditingSensorId(null);
      setEditingSensorName('');
    } catch (error) {
      console.error('Failed to rename sensor:', error);
    }
  };

  const handleCheckboxChange = (sensorId: number) => {
    setSelectedSensorIds(prev =>
      prev.includes(sensorId)
        ? prev.filter(id => id !== sensorId)
        : [...prev, sensorId]
    );
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedSensorIds];
    setSelectedSensorIds([]);
    for (const sensorId of ids) {
      await Promise.resolve(onRemove(zoneId, sensorId));
    }
  };

  if (isLoading || zonesLoading || !zoneSensors) {
    return (
      <div className="p-4">
        <div className="py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
          <span className="inline-block w-5 h-5 align-middle animate-spin border-2 border-blue-400 border-t-transparent rounded-full mr-2"></span>
          Updating sensors...
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 w-full">
      <div className="space-y-2">
        <SensorAssignment
          zoneId={zoneId}
          sensors={sensors}
          onAssign={onAssign}
        />
        {zoneSensors.length === 0 ? (
          <div className="py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
            No sensors assigned to this zone
          </div>
        ) : (
          <div className="mt-3">
            <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100/50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" className="px-2 sm:px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">
                      Sensor
                    </th>
                    <th scope="col" className="px-2 sm:px-6 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 w-16">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-app-inset dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                  {zoneSensors.map((sensor) => (
                    <tr key={sensor.sensor_id} className="hover:bg-gray-50 dark:hover:bg-gray-600/50">
                      <td className="px-2 sm:px-6 py-3">
                        <div className="flex items-center">
                          <Thermometer className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                          {editingSensorId === sensor.sensor_id ? (
                            <form
                              onSubmit={(e) => { e.preventDefault(); handleSaveSensorEdit(sensor.sensor_id); }}
                              className="flex items-center gap-1 flex-1 min-w-0"
                            >
                              <input
                                ref={editInputRef}
                                type="text"
                                value={editingSensorName}
                                onChange={(e) => setEditingSensorName(e.target.value)}
                                className="text-sm font-medium border border-indigo-400 rounded px-2 py-0.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 min-w-0"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Escape' && handleCancelSensorEdit()}
                              />
                              <button type="submit" className="p-1 text-green-600 dark:text-green-400 flex-shrink-0"><Check className="h-4 w-4" /></button>
                              <button type="button" onClick={handleCancelSensorEdit} className="p-1 text-gray-400 flex-shrink-0"><X className="h-3.5 w-3.5" /></button>
                            </form>
                          ) : (
                            <>
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {sensor.sensor_name}
                                </span>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {formatSensorSerial(sensor.sensor_serialNo)}
                                </div>
                              </div>
                              <button
                                onClick={() => handleStartSensorEdit(sensor.sensor_id, sensor.sensor_name)}
                                className="ml-2 p-1 text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300 flex-shrink-0"
                                title="Rename sensor"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-6 py-3 whitespace-nowrap text-right flex items-center justify-end space-x-2">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded mr-2"
                          checked={selectedSensorIds.includes(sensor.sensor_id)}
                          onChange={() => handleCheckboxChange(sensor.sensor_id)}
                          aria-label="Select sensor for bulk delete"
                        />
                        <button
                          onClick={() => onRemove(zoneId, sensor.sensor_id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                          title="Remove sensor from zone"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {zoneSensors.length > 0 && (
              <div className="flex justify-end mt-4">
                <button
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                  onClick={handleDeleteSelected}
                  disabled={selectedSensorIds.length === 0}
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}